/**
 * CommandDashboardLens — Cockpit-only lens-switcher.
 *
 * SCOPE: Wraps KoredorCommandDashboard with an optional Strategic | Operations
 * toggle for super_admin and owner. Other roles see Strategic only (current
 * behavior). Classic skin never reaches this component — the Cockpit gate in
 * CommandDashboard.tsx ensures Classic falls through untouched.
 *
 * The lens state is exported via `useCommandDashboardLens` so the toggle
 * UI can be rendered in the cockpit masthead (right slot) while the
 * lens content is rendered below the header.
 */
import { useEffect, useState } from "react";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { cn } from "@/lib/utils";
import KoredorCommandDashboard from "@/components/dashboard/KoredorCommandDashboard";
import { CommandDashboardServerLensBody } from "@/components/dashboard/CommandDashboardServerLens";
import { OperationsCommandCenter } from "@/components/dashboards/OperationsCommandCenter";
import { Compass, Wrench } from "lucide-react";
import { COMMAND_DASHBOARD_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isCommandDashboardServerPagedRoute, useBrowserLocationSnapshot } from "@/lib/routeScope";

export type Lens = "strategic" | "operations";

const STORAGE_KEY = "command-dashboard:lens";
const LENS_ELIGIBLE_ROLES = new Set(["super_admin", "owner"]);

function readStoredLens(): Lens {
  if (typeof window === "undefined") return "strategic";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "operations" ? "operations" : "strategic";
}

/** Hook providing lens state + eligibility for external composition. */
export function useCommandDashboardLens() {
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const eligible = hasPerm("admin_view");
  const [lens, setLens] = useState<Lens>(() => (eligible ? readStoredLens() : "strategic"));

  useEffect(() => {
    if (!eligible) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, lens);
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [lens, eligible]);

  return { lens, setLens, eligible };
}

/** Standalone segmented control — render anywhere (e.g. cockpit masthead). */
export function CommandDashboardLensSwitch({
  value, onChange, compact = false,
}: { value: Lens; onChange: (v: Lens) => void; compact?: boolean }) {
  return (
    <div
      role="tablist"
      aria-label="Dashboard lens"
      className="inline-flex items-center rounded-lg border border-border/60 bg-muted/30 p-0.5"
    >
      <LensTab
        active={value === "strategic"}
        onClick={() => onChange("strategic")}
        icon={<Compass className="h-3.5 w-3.5" />}
        label="Strategic"
      />
      <LensTab
        active={value === "operations"}
        onClick={() => onChange("operations")}
        icon={<Wrench className="h-3.5 w-3.5" />}
        label="Operations"
      />
    </div>
  );
}

/** Lens content body (no toggle UI). Use when toggle is rendered elsewhere.
 * Operations lens hosts its own toolbar (filters + calendar + native-route
 * deep link) when rendered with `embedded`, so no extra row is needed here. */
export function CommandDashboardLensBody({ lens }: { lens: Lens }) {
  const location = useBrowserLocationSnapshot();
  const serverPagedLens =
    COMMAND_DASHBOARD_SERVER_PAGING_ENABLED &&
    isCommandDashboardServerPagedRoute(location.pathname);

  if (serverPagedLens) return <CommandDashboardServerLensBody lens={lens} />;
  if (lens === "strategic") return <KoredorCommandDashboard />;
  return <OperationsCommandCenter embedded />;
}

export default function CommandDashboardLens() {
  const { lens, setLens, eligible } = useCommandDashboardLens();

  // Non-eligible roles: render Strategic only, no toggle. Same as before.
  if (!eligible) {
    return <KoredorCommandDashboard />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Command Dashboard
          </p>
          <p className="text-[13px] text-muted-foreground/80">
            {lens === "strategic"
              ? "Where the business stands — revenue, channels, reps."
              : "What's blocking work right now — jobs, materials, dispatch."}
          </p>
        </div>
        <CommandDashboardLensSwitch value={lens} onChange={setLens} />
      </div>
      <CommandDashboardLensBody lens={lens} />
    </div>
  );
}

function LensTab({
  active, onClick, icon, label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold transition-all duration-200",
        active
          ? "bg-primary text-primary-foreground shadow-md ring-1 ring-primary/40"
          : "text-muted-foreground hover:text-foreground hover:bg-background/60",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
